import { useState, useEffect, useCallback, useMemo } from "react";
import { Modal } from "@material-ui/core";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import iziToast from "izitoast";

// Import components
import FloatingLabelInput from "./BookingForm/FloatingLabelInput";
import FloatingLabelSelect from "./BookingForm/FloatingLabelSelect";
import Loader from "../../Loader/Loader";
import Loginpopup from "../../Navbar/Loginmodal";

// Import styles
import styles from "./Booking.css";
import "../../App.css";
import './BookingForm/PartyForm.css';
import './BookingForm/Feed.css';
import './BookingForm/FloatingLabelInput.css';
import './BookingForm/FloatingLabelSelect.css';

// Import images
import Googleplay from "./image/Googleplay.png";
import Applestore from "./image/appdownloads.png";
import Rectangle from "./image/Rectangle.png";
import feed1 from "./image/feed-1.webp";
import feed2 from "./image/feed-2.webp";
import feed3 from "./image/feed-3.webp";
import footer1 from '../../Footer/image/footer1.png'
import footer2 from '../../Footer/image/footer2.png'
import promotion from '../../event-view-all-page/image/promotion.webp'
import ImgTableBooking from "../../Assets/group-booking-img.svg"


// Import your custom persistence hooks
import usePersistentForm, { usePersistentJsonState, usePersistentState } from "./usePersistentForm";
import { API_BASE_URL } from "../../utils";

// Validation utilities
const validateForm = (formData, selectedArea) => {
  const errors = [];

  if (!formData.party_date) errors.push("Please select a party date");
  if (!formData.party_time) errors.push("Please select a party time");
  if (!formData.city_id || formData.city === "Select a city") errors.push("Please select a city");
  if (!selectedArea?.id) errors.push("Please select an area");

  // Guest validation
  const male = Number(formData.male) || 0;
  const female = Number(formData.female) || 0;
  const kids = Number(formData.kids) || 0;

  if (male < 0) errors.push("Male guests cannot be negative");
  if (female < 0) errors.push("Female guests cannot be negative");
  if (kids < 0) errors.push("Kids cannot be negative");

  const totalGuests = male + female + kids;
  if (totalGuests === 0) errors.push("At least 1 guest must be selected");

  return errors;
};

function TableBookingForm({ clubId }) {
  const location = useLocation();
  const navigate = useNavigate();

  // Get URL parameters
  const searchParams = new URLSearchParams(location.search);
  const status = searchParams.get("status");

  // User data
  const [userdata, setUserData] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("userData")) || null;
    } catch {
      return null;
    }
  });

  // Form state with persistence using your custom hooks
  const [formData, setFormData] = usePersistentForm("TableBookingFormData", {
    club_id: clubId || "",
    whatsapp: userdata?.whatsapp || "",
    whatsapp_msg: "true",
    city_id: "",
    area_id: "",
    male: "0",
    female: "0",
    kids: "0",
    party_date: "",
    party_time: "",
    lead_source: "website",
    user_id: userdata?.id || "",
    venue_type: "",
    instructions: "",
    city: "",
    agree: false
  });

  // Selected area with persistence
  const [selectedArea, setSelectedArea] = usePersistentJsonState("selectedAreaTable", null);
  const [selectedAreaId, setSelectedAreaId] = usePersistentState("selectedAreaIdTable", null);
  const [location12, setLocation12] = usePersistentState("selectedCityTable", "");
  const [cityId, setCityId] = usePersistentState("cityIdTable", "");

  // UI state
  const [loading, setLoading] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Data state
  const [cityData, setCityData] = useState([]);
  const [areaData, setAreaData] = useState([]);
  const [venueTypes, setVenueTypes] = useState([]);
  const [selectedVenueTypes, setSelectedVenueTypes] = useState([]);

  // Status handling
  useEffect(() => {
    if (status === "success") {
      setShowSuccessModal(true);
    } else if (status === "failed") {
      iziToast.error({
        title: 'Error',
        message: 'Payment failed. Please try again.',
        position: 'topRight',
        timeout: 3000,
      });
    }
  }, [status]);

  // Load cities data
  useEffect(() => {
    const loadCitiesData = async () => {
      try {
        const formData = new FormData();
        formData.append("country_id", "101");

        const stateResponse = await axios.post(
          `${API_BASE_URL}/master/Api/getDataPost/countryWiseState`,
          formData
        );

        const targetStates = ["4007", "4021", "4022"];
        const stateIds = stateResponse.data.data
          .filter(item => targetStates.includes(item.id))
          .map(item => item.id);

        const cityPromises = stateIds.map(stateId => {
          const cityFormData = new FormData();
          cityFormData.append("state_id", stateId);
          return axios.post(
            `${API_BASE_URL}/master/Api/getDataPost/stateWiseCity`,
            cityFormData
          );
        });

        const cityResponses = await Promise.all(cityPromises);
        const allCities = cityResponses.flatMap(response => response.data.data);

        const targetCities = [
          "134404", "134007", "133234", "131821", "58171",
          "132032", "131853", "133230", "132005", "131953"
        ];

        const filteredCities = allCities.filter(city =>
          targetCities.includes(city.id)
        );

        setCityData(filteredCities);
      } catch (error) {
        console.error("Error loading cities:", error);
        iziToast.error({
          title: 'Error',
          message: 'Failed to load cities',
          position: 'topRight',
          timeout: 3000,
        });
      }
    };

    loadCitiesData();
  }, []);

  // Load areas when cityId changes
  useEffect(() => {
    const loadAreas = async () => {
      if (!cityId) {
        setAreaData([]);
        return;
      }

      try {
        const apiFormData = new FormData();
        apiFormData.append("city_id", cityId);

        const response = await axios.post(
          `${API_BASE_URL}/master/Api/getDataPost/AreaLead`,
          apiFormData
        );

        setAreaData(response.data.data || []);
      } catch (error) {
        console.error("Error loading areas:", error);
        setAreaData([]);
      }
    };

    loadAreas();
  }, [cityId]);

  // Load venue types
  useEffect(() => {
    const loadVenueTypes = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/master/APIs/ClubPackage/getVenuType`
        );
        const venues = response.data.data || [];
        setVenueTypes(venues);
        setSelectedVenueTypes(venues); // Select all by default
      } catch (error) {
        console.error("Error loading venue types:", error);
      }
    };

    loadVenueTypes();
  }, []);

  // Update form data when dependencies change
  useEffect(() => {
    const venueTypeIds = selectedVenueTypes.map(venue => venue.id).join(",");

    setFormData(prev => ({
      ...prev,
      club_id: clubId || "",
      user_id: userdata?.id || "",
      whatsapp: userdata?.whatsapp || "",
      city_id: cityId,
      area_id: selectedArea?.id || "",
      venue_type: venueTypeIds
    }));
  }, [clubId, userdata, selectedArea, selectedVenueTypes, cityId]);

  // Form handlers
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;

    // Validate numeric inputs
    if (type === "number" && value !== "" && Number(value) < 0) {
      iziToast.warning({
        title: 'Warning',
        message: 'Negative values are not allowed',
        position: 'topRight',
        timeout: 2000,
      });
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  }, [setFormData]);

  const handleCityChange = useCallback((e) => {
    const selectedCityName = e.target.value;
    const selectedCity = cityData.find(city => city.name === selectedCityName);

    // Update using your custom persistence hooks
    setLocation12(selectedCityName);
    setCityId(selectedCity?.id || "");

    setFormData(prev => ({
      ...prev,
      city: selectedCityName,
      city_id: selectedCity?.id || "",
      area_id: "", // Reset area when city changes
    }));

    // Reset selected area
    setSelectedArea(null);
    setSelectedAreaId(null);
  }, [cityData, setFormData, setLocation12, setCityId, setSelectedArea, setSelectedAreaId]);

  const handleAreaChange = useCallback((e) => {
    const selectedAreaId = e.target.value;
    const area = areaData.find(area => area.id === selectedAreaId);

    // Update using your custom persistence hooks
    setSelectedArea(area || null);
    setSelectedAreaId(selectedAreaId);

    setFormData(prev => ({
      ...prev,
      area_id: selectedAreaId
    }));
  }, [areaData, setFormData, setSelectedArea, setSelectedAreaId]);

  const handleDateChange = useCallback((e) => {
    const selectedDate = e.target.value;
    const today = new Date().toISOString().split("T")[0];

    if (selectedDate < today) {
      iziToast.warning({
        title: 'Warning',
        message: 'Past dates are not allowed',
        position: 'topRight',
        timeout: 2000,
      });
      return;
    }

    setFormData(prev => ({
      ...prev,
      party_date: selectedDate
    }));
  }, [setFormData]);

  // Form validation
  const formErrors = useMemo(() =>
    validateForm(formData, selectedArea),
    [formData, selectedArea]
  );

  const isFormValid = useMemo(() =>
    formErrors.length === 0 && formData.agree,
    [formErrors, formData.agree]
  );

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!isFormValid) {
      const firstError = formErrors[0] || "Please accept terms and conditions";
      iziToast.error({
        title: 'Error',
        message: firstError,
        position: 'topRight',
        timeout: 3000,
      });
      return;
    }

    // Check if user is logged in
    if (!userdata) {
      setShowLoginPopup(true);
      return;
    }

    setLoading(true);

    try {
      const submitFormData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        submitFormData.append(key, value || '');
      });

      const response = await axios.post(
        `${API_BASE_URL}/master/APIs/ClubTable/submitEnquiry`,
        submitFormData
      );

      if (response.data.status === true) {
        // Clear form data from your custom persistence
        setFormData({
          club_id: clubId || "",
          whatsapp: userdata?.whatsapp || "",
          whatsapp_msg: "true",
          city_id: "",
          area_id: "",
          male: "0",
          female: "0",
          kids: "0",
          party_date: "",
          party_time: "",
          lead_source: "website",
          user_id: userdata?.id || "",
          venue_type: "",
          instructions: "",
          city: "",
          agree: false
        });

        // Clear other persistent states
        setSelectedArea(null);
        setSelectedAreaId(null);
        setLocation12("");
        setCityId("");

        // iziToast.success({
        //   title: 'Success',
        //   message: 'Form submitted successfully!',
        //   position: 'topRight',
        //   timeout: 3000,
        // });

        localStorage.removeItem('TableBookingFormData');
        localStorage.removeItem('selectedAreaIdTable');
        localStorage.removeItem('selectedAreaTable');
        localStorage.removeItem('selectedCityTable');
        localStorage.removeItem('cityIdTable');


        // Redirect to success page
        navigate('/table-booking-submitted');
      } else {
        throw new Error(response.data.msg || "Submission failed");
      }
    } catch (error) {
      console.error("Submission error:", error);
      iziToast.error({
        title: 'Error',
        message: error.message || 'Failed to submit form. Please try again.',
        position: 'topRight',
        timeout: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Login handler
  const handleLogin = useCallback((userData) => {
    if (userData) {
      setUserData(userData);
      setFormData(prev => ({
        ...prev,
        user_id: userData.id,
        whatsapp: userData.whatsapp
      }));
      setShowLoginPopup(false);

      // Try to submit form after login
      setTimeout(() => {
        const submitButton = document.querySelector('button[type="submit"]');
        if (submitButton) {
          submitButton.click();
        }
      }, 100);
    }
  }, [setFormData]);

  return (
    <div className="Package-lead-page-container">
      {loading && <Loader />}
      {showLoginPopup && <Loginpopup clbk={handleLogin} />}

      <div className={styles.Container}>
        <div className="feed-section-container width-1050">
          <div className='feed-section'>
            {/* Hero Section */}
            <div className='feed-section-img feed-image-wrapper'>
              <div className="feed-img-section">

                   <img src={ImgTableBooking} alt="Clubs" />
                </div>
            </div>

            {/* Form Section */}
            <div className='feed-form-section'>
              <div className="tabs-sec-nav">
                <Link to='/package-lead-form'>
                  <div className="tabs-sec-nav-box">
                    <h1>Party Package</h1>
                  </div>
                </Link>
                <Link to='/table-book-form'>
                  <div className="tabs-sec-nav-box active">
                    <h1>Table Reservation</h1>
                  </div>
                </Link>
              </div>

              <h2>Tell Us When Is The Party</h2>

              <div className="pplf-form-container">
                <form onSubmit={handleSubmit} className="party-form">
                  <FloatingLabelInput
                    type="date"
                    name="party_date"
                    label="Date"
                    value={formData.party_date}
                    onChange={handleDateChange}
                    min={new Date().toISOString().split("T")[0]}
                    required
                  />

                  <FloatingLabelInput
                    type="time"
                    name="party_time"
                    label="Time"
                    value={formData.party_time}
                    onChange={handleInputChange}
                    required
                  />

                  <div className="guest-group">
                    <div className="guest-g-input">
                      <FloatingLabelInput
                        type="number"
                        name="male"
                        label="Male Guests"
                        value={formData.male}
                        onChange={handleInputChange}
                        min="0"
                        required
                      />
                      {/* <span className="small-text-form">21 years and above</span> */}
                    </div>

                    <div className="guest-g-input">
                      <FloatingLabelInput
                        type="number"
                        name="female"
                        label="Female Guests"
                        value={formData.female}
                        onChange={handleInputChange}
                        min="0"
                        required
                      />
                      {/* <span className="small-text-form">21 years and above</span> */}
                    </div>
                  </div>

                  <div>
                    <FloatingLabelInput
                      type="number"
                      name="kids"
                      label="Kids"
                      value={formData.kids}
                      onChange={handleInputChange}
                      min="0"
                      required
                    />
                    {/* <span className="small-text-form">5 years & below</span> */}
                  </div>

                  <div className="dropdown-group">
                    <FloatingLabelSelect
                      label="City"
                      name="city"
                      value={location12}
                      onChange={handleCityChange}
                      options={[
                        { id: "", name: "Select a city" },
                        ...cityData
                      ]}
                      valueKey="name"
                      required
                    />

                    <FloatingLabelSelect
                      name="area"
                      label="Area"
                      value={selectedAreaId || ""}
                      onChange={handleAreaChange}
                      options={[
                        { id: "", name: "Select an area" },
                        ...areaData
                      ]}
                      valueKey="id"
                      required
                    />
                  </div>

                  <FloatingLabelInput
                    type="text"
                    name="instructions"
                    label="Special Instructions"
                    value={formData.instructions}
                    onChange={handleInputChange}
                  />

                  <div className="checkbox">
                    <input
                      type="checkbox"
                      id="agreeCheckbox"
                      name="agree"
                      checked={formData.agree}
                      onChange={handleInputChange}
                      required
                    />
                    <span>
                      I agree to the{' '}
                      <Link to="/privacy-policy">Privacy Policy</Link> and{' '}
                      <Link to="/term-conditions-policy">Terms & Conditions</Link>.
                    </span>
                  </div>

                  <button
                    type="submit"
                    disabled={!isFormValid || loading}
                    className={!isFormValid ? "package-and-table-btn-disabled" : ""}
                  >
                    {loading ? 'Submitting...' : 'Reserve Now'}
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* Stats Section */}
          <div className='container'>
         

            <div className='f-feed'>
                <div className='f-feed-line'>
                  <div className='f-feed-text'>
                      <div className='f-feed-img'>
                      <img src={feed1} alt="club-image" loading='lazy' />
                    </div>
                    <h1>1000+</h1>
                    <p>Clubs </p>
                  </div>
                  
                </div>
                <div className='v-line'></div>
                <div className='f-feed-line'>
                  <div className='f-feed-text'>
                      <div className='f-feed-img'>
                      <img src={feed2} alt="Packages-image" loading='lazy' />
                    </div>
                    <h1>5,000 +</h1>
                    <p>F&B Packages </p>
                  </div>
                  
                </div>
                <div className='v-line'></div>

                <div className='f-feed-line'>
                  <div className='f-feed-text'>
                      <div className='f-feed-img'>
                      <img src={feed3} alt="Customers-image" loading='lazy' />
                    </div>
                    <h1>12,000 +</h1>
                    <p>Happy Customers </p>
                  </div>
                  
                </div>
              </div>
          </div>



          <div className="event-cards-container">
            <div className='event-detail-page-design-bottom-promotional-banner'>
              <div className='event-detail-page-design-bottom-promotional-banner-content'>
                <div className='event-detail-page-design-bottom-promotional-banner-content-heading'>
                  <h1>🎉 Get 500 carrots🥕 instantly → Download the App</h1>
                </div>
                <div className='event-detail-page-design-bottom-promotional-banner-content-images'>
                  <a href="https://apps.apple.com/in/app/partywitty/id6478442502">
                    <img src={footer1} alt="" />
                  </a>
                  <a href="https://play.google.com/store/apps/details?id=com.party.partywitty_guest">
                    <img src={footer2} alt="" />
                  </a>
                </div>
              </div>
              <div className='event-detail-page-design-bottom-promotional-banner-image'>
                <img src={promotion} alt="" />
              </div>
            </div>
          </div>

          <div className='package-page-design-bottom-footer-tags-section'>
            <div className='package-page-detail-page-design-bottom-footer-tags'>
              <div className='package-page-detail-page-design-bottom-footer-tags-text'>
                <h1>Instant Table Booking – Best Spots, Best Deals!</h1>
                <p>Skip the wait and book your party table instantly with PartyWitty! Discover top-rated venues in Delhi NCR for birthdays, get-togethers, or weekend hangouts — all with real-time availability and unbeatable deals on food and drinks. No more endless calls or last-minute stress. Whether it’s a casual night out or a grand celebration, lock in the best spots with exclusive offers. PartyWitty makes party planning simple, fast, and budget-friendly — so the fun starts even before the party does!</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <Modal
        open={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          navigate('/');
        }}
      >
        <div className="modal-container">
          <div className="modal-content">
            <button
              className="modal-button"
              onClick={() => {
                setShowSuccessModal(false);
                navigate('/');
              }}
            >
              ×
            </button>
            <dotlottie-player
              src="https://lottie.host/6b9fc96a-5c59-464a-ad26-5782e3a54022/EFWiS28KVM.json"
              background="transparent"
              speed="1"
              style={{ height: "300px" }}
              loop
              autoplay
            />
            <p className="modal-message">Form submitted successfully!</p>
            <div className="about-us-playstore-image">
              <Link to="https://play.google.com/store/apps/details?id=com.party.partywitty_guest">
                <img src={Googleplay} alt="Play Store" />
              </Link>
              <Link to="https://apps.apple.com/app/partywitty-guest/id123456789">
                <img src={Applestore} alt="App Store" />
              </Link>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default TableBookingForm;